import { ConfigType, setConfig } from './config';

import { signIn, signOut } from './services';
import * as Sentry from '@sentry/react';
import { clearAuthState } from './auth-utils';

export { clearAuthState } from './auth-utils';

let _handlingAuthFailure = false;

export function withConcurrentAuthFailureGuard(action: () => void): void {
  if (_handlingAuthFailure) return;
  _handlingAuthFailure = true;
  action();
}

export function getIsHandlingAuthFailure(): boolean {
  return _handlingAuthFailure;
}

type LoginOptions = {
  redirect?: string;
  userId?: number;
  organizationAdminId?: number;
  reason?: string;
};

export function addAuthBreadcrumb(message: string, data: Record<string, unknown>) {
  Sentry.addBreadcrumb({
    category: 'auth',
    level: 'info',
    message,
    data,
  });
}

export function initAuth(config: ConfigType) {
  setConfig(config);
}

// Route guards and the Apollo errorLink can both react to the same auth
// failure and race each other into parallel WorkOS authorize navigations
// (each with its own PKCE nonce).
let _loginNavigationInFlight = false;

export function login({ redirect, userId, organizationAdminId, reason }: LoginOptions = {}) {
  if (_loginNavigationInFlight) {
    addAuthBreadcrumb('login_skipped_navigation_in_flight', {
      reason: reason || 'unspecified',
    });
    return Promise.resolve();
  }
  _loginNavigationInFlight = true;
  const redirectAfterLogin = redirect || `${window.location.pathname}${window.location.search}`;
  addAuthBreadcrumb('login_called', {
    reason: reason || 'unspecified',
    currentPath: window.location.pathname,
    currentSearchKeys: [...new URLSearchParams(window.location.search).keys()].sort(),
    redirectAfterLogin,
    hasUserId: Boolean(userId),
    hasOrganizationAdminId: Boolean(organizationAdminId),
    historyLength: window.history.length,
  });
  localStorage.setItem('redirectAfterLogin', redirectAfterLogin);
  return signIn({ userId, organizationAdminId }).catch((error: unknown) => {
    _loginNavigationInFlight = false;
    throw error;
  });
}

export async function logout(): Promise<void> {
  try {
    // signOut() handles clearAuthState() internally before navigating
    await signOut({});
  } catch {
    // If signOut fails (e.g. network error before clearAuthState ran),
    // ensure local auth state is cleaned up before redirecting
    clearAuthState();
    window.location.href = '/';
  }
}
